// Team calendar: one row per person, one column per day.

import { useEffect, useRef, useState } from "react";
import {
  daysInMonth,
  fmtMonth,
  fmtRange,
  fmtShort,
  formatDays,
  leaveYearLabel,
  leaveYearOf,
  monthISO,
  weekdayShort,
} from "../lib/dates";
import { ACTIVE_STATUSES, TYPE_LIST, balanceFor, displayType, isNonWorking, partOnDay, partSuffix } from "../lib/leave";
import { useStore } from "../store/StoreProvider";
import { useCtx } from "../store/useCtx";
import type { LeaveRequest, User } from "../types";
import { Avatar, byName, plural, typeStyle } from "../ui/bits";
import { useDialogs } from "../ui/dialogs";
import { Icon } from "../ui/Icon";

export function Calendar() {
  const ctx = useCtx();
  const store = useStore();
  const { openRequest, openDetail } = useDialogs();
  const [{ y: initialYear, m: initialMonth }] = useState(() => {
    const [y, m] = ctx.today.split("-").map(Number) as [number, number];
    return { y, m };
  });
  const [y, setY] = useState(initialYear);
  const [m, setM] = useState(initialMonth);
  const [dept, setDept] = useState("all");
  const scroller = useRef<HTMLDivElement>(null);

  const shiftMonth = (delta: number) => {
    const index = y * 12 + (m - 1) + delta;
    setY(Math.floor(index / 12));
    setM((index % 12) + 1);
  };
  const goToday = () => {
    setY(initialYear);
    setM(initialMonth);
  };

  const dim = daysInMonth(y, m);
  const first = monthISO(y, m, 1);
  const last = monthISO(y, m, dim);
  const days = Array.from({ length: dim }, (_, i) => monthISO(y, m, i + 1));
  const canGoBack = first > store.windowStart();
  const leaveYear = leaveYearOf(ctx.today, ctx.settings.leaveYearStartMonth);
  const yearLabel = leaveYearLabel(leaveYear, ctx.settings.leaveYearStartMonth);

  // Bring today's column into view when the month changes; for other months start at the 1st.
  useEffect(() => {
    const sc = scroller.current;
    if (!sc) return;
    const today = sc.querySelector<HTMLElement>(".dayhead.today");
    sc.scrollLeft = today ? Math.max(0, today.offsetLeft - sc.clientWidth / 2) : 0;
  }, [y, m]);

  const shown = ctx.activeUsers.filter((u) => dept === "all" || u.departmentId === dept);
  const groups = [
    ...ctx.departments.map((d) => ({ id: d.id as string | null, name: d.name })),
    { id: null, name: "No department" },
  ]
    .map((g) => ({ ...g, people: shown.filter((u) => (u.departmentId || null) === g.id).sort(byName) }))
    .filter((g) => g.people.length)
    .sort((a, b) => Number(a.id === null) - Number(b.id === null) || a.name.localeCompare(b.name, "en-GB"));

  const inMonth = ctx.requests.filter((r) => ACTIVE_STATUSES.includes(r.status) && r.end >= first && r.start <= last);
  const byUser = new Map<string, LeaveRequest[]>();
  for (const r of inMonth) byUser.set(r.userId, [...(byUser.get(r.userId) ?? []), r]);

  // People away per day (approved leave only), for the footer row.
  const shownIds = new Set(shown.map((u) => u.id));
  const away = days.map((d) => {
    if (isNonWorking(d, ctx.bh)) return 0;
    let n = 0;
    for (const r of inMonth) {
      if (r.status !== "approved" || !shownIds.has(r.userId)) continue;
      const p = partOnDay(r, d);
      n += p === "full" ? 1 : p ? 0.5 : 0;
    }
    return n;
  });

  const dayClass = (d: string) =>
    [isNonWorking(d, ctx.bh) && !ctx.bh.has(d) ? "we" : "", ctx.bh.has(d) ? "bh" : "", d === ctx.today ? "today" : ""]
      .filter(Boolean)
      .join(" ");

  const cell = (u: User, d: string) => {
    const nonWorking = isNonWorking(d, ctx.bh);
    const leaves = nonWorking
      ? []
      : (byUser.get(u.id) ?? [])
          .map((r) => ({ r, p: partOnDay(r, d) }))
          .filter((x): x is { r: LeaveRequest; p: NonNullable<typeof x.p> } => !!x.p);
    const bookable = !nonWorking && !leaves.length && (u.id === ctx.me.id || ctx.me.role === "hr");
    return (
      <td
        key={d}
        className={`day ${dayClass(d)} ${bookable ? "bookable" : ""}`.trim()}
        {...(bookable && {
          onClick: () => openRequest({ date: d, userId: u.id }),
          title: `Book leave for ${u.name} on ${fmtShort(d)}`,
        })}
      >
        <div className="cell">
          {leaves.map(({ r, p }) => {
            const t = displayType(r, ctx.me, ctx);
            const part = partSuffix(r);
            const label = `${u.name}, ${t.label}, ${fmtRange(r.start, r.end)}${part ? `, ${part}` : ""}, ${r.status}`;
            return (
              <button
                key={r.id}
                type="button"
                className={`lv lv-${p} ${r.status === "pending" ? "lv-pending" : ""}`.trim()}
                style={typeStyle(t)}
                data-l={t.letter}
                onClick={() => openDetail(r.id)}
                aria-label={label}
                title={label}
              />
            );
          })}
        </div>
      </td>
    );
  };

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-nav">
          <button
            type="button"
            className="icon-btn"
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
            disabled={!canGoBack}
          >
            <Icon name="chevron-left" />
          </button>
          <h2 className="cal-month" aria-live="polite">
            {fmtMonth(y, m)}
          </h2>
          <button type="button" className="icon-btn" onClick={() => shiftMonth(1)} aria-label="Next month">
            <Icon name="chevron-right" />
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={goToday}>
            Today
          </button>
        </div>
        <div className="inline-field">
          <label htmlFor="cal-dept">Department</label>
          <select id="cal-dept" value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value="all">Everyone</option>
            {ctx.departments
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name, "en-GB"))
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      <ul className="legend" aria-label="Key">
        <li title={`Holiday days remaining for ${yearLabel}, after approved and pending bookings`}>
          <span className="days-left" aria-hidden="true">
            24
          </span>
          Holiday days left
        </li>
        {TYPE_LIST.map((t) => (
          <li key={t.id}>
            <span className="swatch" style={typeStyle(t)} data-l={t.letter} aria-hidden="true" />
            {t.label}
          </li>
        ))}
        <li>
          <span
            className="swatch swatch-pending"
            style={typeStyle({ color: "#AAF6CD", accent: "#009257" })}
            aria-hidden="true"
          />
          Awaiting approval
        </li>
        <li>
          <span className="swatch swatch-bh" aria-hidden="true" />
          Bank holiday
        </li>
      </ul>

      <div
        ref={scroller}
        className="cal-scroll card"
        tabIndex={0}
        role="region"
        aria-label={`Team calendar for ${fmtMonth(y, m)}. Scrolls sideways on small screens.`}
      >
        <table className="cal" style={{ "--cols": dim } as React.CSSProperties}>
          <caption className="sr-only">
            Who is off in {fmtMonth(y, m)}. Each row is a person and each column a day. Green badges show holiday days
            remaining for the current leave year, {yearLabel}, after approved and pending bookings.
          </caption>
          <thead>
            <tr>
              <th scope="col" className="person head-corner">
                {plural(shown.length, "person", "people")}
              </th>
              {days.map((d) => (
                <th key={d} scope="col" className={`dayhead ${dayClass(d)}`.trim()} title={ctx.bh.get(d)}>
                  <span className="dow">{weekdayShort(d).slice(0, 2)}</span>
                  <span className="dom">{Number(d.slice(8))}</span>
                  {ctx.bh.has(d) && <span className="sr-only">Bank holiday: {ctx.bh.get(d)}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <GroupRows key={g.id ?? "none"} name={g.name} span={dim + 1}>
                {g.people.map((u) => {
                  const remaining = balanceFor(u, leaveYear, ctx).remaining;
                  const balanceLabel = `${formatDays(remaining)} holiday day${remaining === 1 ? "" : "s"} left for ${yearLabel}, including pending bookings`;
                  return (
                    <tr key={u.id} className={u.id === ctx.me.id ? "is-me" : undefined}>
                      <th scope="row" className="person">
                        <div className="who">
                          <Avatar name={u.name} photoUrl={u.photoUrl} />
                          <span className="pname" title={u.name}>
                            {u.name}
                          </span>
                          {u.id === ctx.me.id && <span className="sr-only"> (you)</span>}
                          <span className="days-left" aria-label={balanceLabel} title={balanceLabel}>
                            {formatDays(remaining)}
                          </span>
                        </div>
                      </th>
                      {days.map((d) => cell(u, d))}
                    </tr>
                  );
                })}
              </GroupRows>
            ))}
            {!groups.length && (
              <tr>
                <td colSpan={dim + 1} className="empty-cell">
                  No one to show for this department.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" className="person">
                Away
              </th>
              {away.map((n, i) => (
                <td key={days[i]} className={`count ${dayClass(days[i]!)}`.trim()}>
                  {n ? formatDays(n) : ""}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="hint">
        Click a leave block for details. Click an empty working day in your row to book leave, or use the Request leave
        button.
      </p>
    </>
  );
}

function GroupRows({ name, span, children }: { name: string; span: number; children: React.ReactNode }) {
  return (
    <>
      <tr className="group">
        <th scope="rowgroup" colSpan={span}>
          <span className="group-label">{name}</span>
        </th>
      </tr>
      {children}
    </>
  );
}
